/**
 * The `reasoning` wire contract — read directly off a `dh_ui` envelope's
 * `payload` whose `kind` is `"reasoning"`, never off raw JSON. Design doc
 * §10.3 "Контракт кадра".
 *
 * Two shapes share one envelope: `{state:"round", n, text}` — one already
 * sanitised, already-Russian summary of a single model round — and
 * `{state:"done", rounds, ms}`, emitted in `on_turn_end` whenever the flag is
 * on, even when zero summaries ever went out, so it can close the block's
 * spinner. `n` is 1-based and exists ONLY so the client can reorder frames
 * that finished out of order (the summariser calls are async and race each
 * other) — never read as a count. Idempotency is by `n`: a repeated frame for
 * the same round overwrites its row rather than adding a second one, which
 * lives in `activityStore`, not here — same split as `activity/types.ts`.
 *
 * Sourced from `graphs/peterhof_agent/reasoning_content` this way, on
 * purpose: the frontend never reads a message's own `reasoning`/
 * `reasoning_content` field on the canvas path (that stays behind `?debug=1`,
 * §10.3/§10.5) — everything shown here is the backend's own sanitised prose.
 */

export type ReasoningFrame =
  | { state: 'round'; n: number; text: string }
  | { state: 'done'; rounds: number; ms: number | null };

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/**
 * `payload` off a `kind: "reasoning"` envelope, or `null` when it is not a
 * shape this client understands — the caller drops the frame rather than
 * guessing (same discipline as `activity/types.ts`'s `readActivityFrame`).
 */
export function readReasoningFrame(payload: unknown): ReasoningFrame | null {
  if (!isObject(payload)) return null;
  const state = str(payload.state);
  if (state === 'round') {
    const n = num(payload.n);
    const text = str(payload.text);
    if (n === null || !text) return null; // §10.3: both are always present
    return { state: 'round', n, text };
  }
  if (state === 'done') {
    const rounds = num(payload.rounds) ?? 0;
    return { state: 'done', rounds, ms: num(payload.ms) };
  }
  return null;
}
